import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { Meta } from "interfaces/api.interface";
import { ILabelJobHistory } from "models/labelJob/labelJob.model";
import { fetchLabelJobsHistory } from "services/api/labels";
import { AppThunk, RootState } from "store";
import { isModalClosed } from "store/modalSlice/modalSlice.actions";
import { modals } from "store/modalSlice/modalSlice.types";
import { FetchLabelJobsHistoryResponseDto } from "services/api/labels/dtos/fetch-label-jobs.response.dto";
import { notifyBugReporter } from "services/bugReporter";
import { DEFAULT_META } from "constants/misc";

export interface ICurrentLabelJobsSlice {
  loadingStatus: ApiLoadingStatus;
  items: ILabelJobHistory[];
  meta: Meta<{ downloaded: number; notDownloaded: number }>;
}

export const CurrentLabelJobsSliceInitialState: ICurrentLabelJobsSlice = {
  loadingStatus: ApiLoadingStatus.IDLE,
  items: [],
  meta: {
    ...DEFAULT_META,
    downloaded: 0,
    notDownloaded: 0,
  },
};

export const currentLabelJobsSlice = createSlice({
  name: "currentLabelJobsSlice",
  initialState: CurrentLabelJobsSliceInitialState,
  reducers: {
    setData: (
      state,
      action: PayloadAction<FetchLabelJobsHistoryResponseDto>
    ) => {
      state.items =
        action.payload.data || CurrentLabelJobsSliceInitialState.items;

      state.meta =
        action.payload.meta || CurrentLabelJobsSliceInitialState.meta;

      state.loadingStatus = ApiLoadingStatus.SUCCESS;
    },
    setStatus: (
      state,
      action: PayloadAction<ICurrentLabelJobsSlice["loadingStatus"]>
    ) => {
      state.loadingStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isModalClosed,
      (state, action: { payload: { modalName: modals } }) => {
        if (action.payload.modalName === "createSingleLabel") {
          Object.assign(state, CurrentLabelJobsSliceInitialState);
        }
      }
    );
  },
});

export const { setData, setStatus } = currentLabelJobsSlice.actions;

export const fetchCurrentLabelJobsThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setStatus(ApiLoadingStatus.LOADING));
    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (salesAccountUUID) {
      const { data, error, meta } = await fetchLabelJobsHistory(
        salesAccountUUID,
        {
          // The drawer lists the latest 50 jobs and never shows a total, so
          // skip the server-side count of every job the account ever created.
          // That count is what kept the drawer on its spinner for minutes
          // whenever the database host was slow.
          filters: { withCount: "false" },
        }
      );

      try {
        if (error) {
          dispatch(setStatus(ApiLoadingStatus.FAILED));
        }

        if (!error) {
          dispatch(setData({ data, meta }));
          dispatch(setStatus(ApiLoadingStatus.SUCCESS));
        }
      } catch (e) {
        notifyBugReporter(e);
        dispatch(setStatus(ApiLoadingStatus.FAILED));
      }
    } else {
      // Nothing to fetch without an account. Leaving the status on LOADING
      // would keep the Labels drawer on its spinner with no way to retry.
      dispatch(setStatus(ApiLoadingStatus.FAILED));
    }
  };
};
